import { IYieldCompounding } from '~/@api/generated-api-types-project/Api'
import { nonNullable } from '~/@lib/utils/type.utils'
import { formatAmount } from '~/@lib/utils/number.utils'
import { Text } from '~/@mid_ui/typography/Text'
import { GoogleMap } from '~/@mid_ui/google-map/GoogleMap'
import type { ProjectSchema } from '~/@pages_investing/projects/_project_types/project.types'
import { DetailHeading } from '../detail-heading/DetailHeading'
import { projectDetailTooltipByType } from '../detail-tooltips/detailTooltips'
import { DetailItem } from '../detail-item/DetailItem'
import { customTextRow } from '../detail-html-value/customTextRow'
import { hasCustomText } from '../detail-html-value/hasCustomText'
import { getProjectEncumberanceLabel } from './getProjectEncumberanceLabel'
import { getProjectPropertyRentalContracts } from './getProjectPropertyRentalContracts'
import { getMapCoordinates } from './utils'

type Props = {
  project: ProjectSchema
}

function EstablishedPropertyInfo({ project }: Props) {
  const { propertyInfo } = project.collateralInfo ?? {}
  const {
    expertValueEstimate,
    expertValueEstimateCustom,
    marketValue,
    marketValueCustom,
    hasInfoText,
    infoText,
  } = propertyInfo ?? {}

  const items = [
    {
      heading: (
        <DetailHeading
          title="Typ nehnuteľnosti:"
          tooltip={projectDetailTooltipByType.propertyType}
        />
      ),
      value: <Text>{propertyInfo?.type || 'N/A'}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Katastrálne územie:"
          tooltip={projectDetailTooltipByType.cadastralArea}
        />
      ),
      value: <Text>{propertyInfo?.cadastralArea || 'N/A'}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Vlastníci:"
          tooltip={projectDetailTooltipByType.owners}
        />
      ),
      value: <Text>{propertyInfo?.owners || 'N/A'}</Text>,
    },
    {
      heading: (
        <DetailHeading
          title="Ťarchy:"
          tooltip={projectDetailTooltipByType.encumbrances}
        />
      ),
      value: (
        <Text>{getProjectEncumberanceLabel(propertyInfo?.encumbrance)}</Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Nájomné zmluvy:"
          tooltip={projectDetailTooltipByType.rentalContracts}
        />
      ),
      value: (
        <Text>
          {getProjectPropertyRentalContracts(propertyInfo?.rentalContracts)}
        </Text>
      ),
    },
    {
      heading: (
        <DetailHeading
          title="Iné záväzky:"
          tooltip={projectDetailTooltipByType.otherLiabilities}
        />
      ),
      value: <Text>{propertyInfo?.otherLiabilities || 'N/A'}</Text>,
    },
    hasCustomText(expertValueEstimateCustom)
      ? customTextRow({
          title: 'Znalecká hodnota:',
          tooltip: projectDetailTooltipByType.expertValueEstimate,
          html: expertValueEstimateCustom,
        })
      : nonNullable(expertValueEstimate)
        ? {
            heading: (
              <DetailHeading
                title="Znalecká hodnota:"
                tooltip={projectDetailTooltipByType.expertValueEstimate}
              />
            ),
            value: <Text>{formatAmount(expertValueEstimate)}</Text>,
          }
        : null,
    hasCustomText(marketValueCustom)
      ? customTextRow({
          title: 'Trhová hodnota:',
          tooltip: projectDetailTooltipByType.marketValue,
          html: marketValueCustom,
        })
      : nonNullable(marketValue)
        ? {
            heading: (
              <DetailHeading
                title="Trhová hodnota:"
                tooltip={projectDetailTooltipByType.marketValue}
              />
            ),
            value: <Text>{formatAmount(marketValue)}</Text>,
          }
        : null,
  ]

  const coordinates = getMapCoordinates(
    project.collateralInfo.propertyInfo?.mapCoordinates
  ) ?? { lat: 48.1606746, lng: 17.1500082 }
  const isProjectYieldCompoundingAnnually =
    project.yieldCompounding === IYieldCompounding.Annually

  return (
    <DetailItem
      sectionId="establishedInfo"
      title="Info o založenej nehnuteľnosti"
      rows={items.filter(nonNullable)}
      extraContent={
        isProjectYieldCompoundingAnnually &&
        coordinates && (
          <div className="flex flex-col items-start justify-center w-full h-75 relative">
            <Text>Lokalita</Text>
            <GoogleMap position={coordinates} />
          </div>
        )
      }
      customContent={
        hasInfoText ? (
          <Text className="whitespace-pre-wrap">{infoText}</Text>
        ) : undefined
      }
    />
  )
}

export { EstablishedPropertyInfo }
